import { createBrowserHistory } from 'history';
import * as NewRelicBrowser from 'new-relic-browser';
import { parse, stringify } from 'qs';
import { createElement } from 'react';
import { hydrate, render } from 'react-dom';
import { addLocaleData } from 'react-intl';
import * as enLocaleData from 'react-intl/locale-data/en';
import * as jaLocaleData from 'react-intl/locale-data/ja';
import Store from 'repatch';

import GoogleAnalytics from './adapter/GoogleAnalytics';
import GraphQLClient from './adapter/GraphQLClient';
import HttpClient from './adapter/HttpClient';
import Config from './core/Config';
import State from './core/State';
import baseIntiialState from './initialState';
import createKukerEmitterMiddleware from './kukerEmitterMiddleware';
import setLocation from './reducer/setLocation';
import { registerCustomElements } from './register-custom-elements';
import ApiErrorHandler from './service/ApiErrorHandler';
import ErrorPresenter from './service/ErrorPresenter';
import ErrorReporter from './service/ErrorReporter';
import IntercomService from './service/IntercomService';
import MessageDispatcher from './service/MessageDispatcher';
import PerformanceMeasure from './service/PerformanceMeasure';
import AccountWebApi from './service/persistent/AccountWebApi';
import AcctWebApi from './service/persistent/AcctWebApi';
import AgentWebApi from './service/persistent/AgentWebApi';
import AuthenticationWebApi from './service/persistent/AuthenticationWebApi';
import BfWebApi from './service/persistent/BfWebApi';
import ConfigurationStorage from './service/persistent/ConfigurationStorage';
import OrganizationWebApi from './service/persistent/OrganizationWebApi';
import OtpRecoveryCodeWebApi from './service/persistent/OtpRecoveryCodeWebApi';
import UserPersonalWebApi from './service/persistent/UserPersonalWebApi';
import UserWebApi from './service/persistent/UserWebApi';
import TranslationBackend from './service/TranslationBackend';
import AccountUsecase from './usecase/AccountUsecase';
import AcctUsecase from './usecase/AcctUsecase';
import AgentUsecase from './usecase/AgentUsecase';
import AuthenticationUsecase from './usecase/AuthenticationUsecase';
import BfUsecase from './usecase/BfUsecase';
import ConfigurationChangingUsecase from './usecase/ConfigurationChangingUsecase';
import DrawerOpenUsecase from './usecase/DrawerOpenUsecase';
import OrganizationUsecase from './usecase/OrganizationUsecase';
import OtpRecoveryCodeUsecase from './usecase/OtpRecoveryCodeUsecase';
import QuestionnaireUsecase from './usecase/QuestionnaireUsecase';
import UserPersonalUsecase from './usecase/UserPersonalUsecase';
import UserUsecase from './usecase/UserUsecase';
import execSitejs from './utility/execSitejs';
import Root from './view/Root';

declare global {
  interface Window {
    newrelic: typeof NewRelicBrowser;
  }
}

const main = () => {
  registerCustomElements(window.customElements);
  addLocaleData([...enLocaleData, ...jaLocaleData, { locale: 'ja-JP', parentLocale: 'ja' } as any]);

  const config: Config = (window as any).__kaizenconfig__;
  const appVersion: string = (window as any).__kaizenversion__;
  const { done } = parse(window.location.search, { ignoreQueryPrefix: true });

  if (done && done.length > 0) {
    window.sessionStorage.setItem('done', done);
  }

  const initialState = {
    ...baseIntiialState,
    authentication: {
      ...baseIntiialState.authentication,
      done: done,
    },
    location: {
      pathname: window.location.pathname,
      search: window.location.search,
    },
  };

  const history = createBrowserHistory();

  const store = new Store<State>(initialState);

  // Adapter

  const googleAnalytics = new GoogleAnalytics((window as any).gtag, config.GA_TRACKING_ID);
  const performanceMeasure = new PerformanceMeasure({ googleAnalytics });

  const idGraphqlClient = new GraphQLClient({
    endpointUrl: config.ID_API_GRAPHQL_ENDPOINT,
  });

  const idApiHttpClient = new HttpClient({
    baseUrl: config.ID_API_BASE_URL,
  });

  const acctApiHttpClient = new HttpClient({
    baseUrl: config.ACCT_API_BASE_URL,
  });

  const bfApiHttpClient = new HttpClient({
    baseUrl: config.BF_API_BASE_URL,
  });

  // Service
  const errorPresenter = new ErrorPresenter({ store });

  const errorReporter = new ErrorReporter({
    store,
    dsn: config.SENTRY_API_DSN,
    environment: config.STAGE,
    release: appVersion,
  });

  window.newrelic.addRelease(config.STAGE, appVersion);
  window.onunhandledrejection = (data: any) => {
    errorReporter.reportError(data.reason);
  };

  const messageDispatcher = new MessageDispatcher({ store });

  const translationBackend = new TranslationBackend({ appVersion });

  const configurationStorage = new ConfigurationStorage({ storage: window.localStorage });

  const userWebApi = new UserWebApi({ idGraphqlClient, httpClient: idApiHttpClient });
  const authenticationWebApi = new AuthenticationWebApi({ httpClient: idApiHttpClient });
  const otpRecoveryCodeWebApi = new OtpRecoveryCodeWebApi({ idGraphqlClient });
  const agentWebApi = new AgentWebApi({ idGraphqlClient });
  const userPersonalWebApi = new UserPersonalWebApi({ idGraphqlClient });
  const organizationWebApi = new OrganizationWebApi({ idGraphqlClient, httpClient: idApiHttpClient });
  const accountWebApi = new AccountWebApi({ idGraphqlClient });
  const acctWebApi = new AcctWebApi({ httpClient: acctApiHttpClient });
  const bfWebApi = new BfWebApi({ httpClient: bfApiHttpClient });

  const apiErrorHandler = new ApiErrorHandler({ errorPresenter, errorReporter });

  // intercom
  const intercomService = new IntercomService(() => (window as any).Intercom, config.INTERCOM_APP_ID);

  // Usecase
  const configurationChangingUsecase = new ConfigurationChangingUsecase({
    store,
    configurationStorage,
    translationBackend,
  });

  const authenticationUsecase = new AuthenticationUsecase({
    store,
    authenticationWebApi,
    userWebApi,
    configurationChangingUsecase: configurationChangingUsecase,
    intercomService,
    messageDispatcher,
    apiErrorHandler,
  });

  const otpRecoveryCodeUsecase = new OtpRecoveryCodeUsecase({
    apiErrorHandler,
    otpRecoveryCodeWebApi,
    messageDispatcher,
  });

  const agentUsecase = new AgentUsecase({
    store,
    apiErrorHandler,
    agentWebApi,
    messageDispatcher,
  });

  const userUsecase = new UserUsecase({
    store,
    apiErrorHandler,
    userWebApi,
    messageDispatcher,
    authenticationUsecase,
  });

  const userPersonalUsecase = new UserPersonalUsecase({
    store,
    apiErrorHandler,
    userPersonalWebApi,
    messageDispatcher,
  });

  const organizationUsecase = new OrganizationUsecase({
    store,
    apiErrorHandler,
    organizationWebApi,
    messageDispatcher,
  });

  const bfUsecase = new BfUsecase({
    store,
    apiErrorHandler,
    bfWebApi,
    messageDispatcher,
  });

  const acctUsecase = new AcctUsecase({
    store,
    apiErrorHandler,
    acctWebApi,
    messageDispatcher,
    bfUsecase,
  });

  const accountUsecase = new AccountUsecase({
    store,
    apiErrorHandler,
    accountWebApi,
    messageDispatcher,
    organizationUsecase,
  });

  const drawerOpenUsecase = new DrawerOpenUsecase({
    store,
  });

  const questionnaireUsecase = new QuestionnaireUsecase({
    intercomService,
  });

  const dependency = {
    errorReporter,
    errorPresenter,
    messageDispatcher,
    performanceMeasure,
    authenticationUsecase,
    configurationChangingUsecase,
    userUsecase,
    otpRecoveryCodeUsecase,
    agentUsecase,
    drawerOpenUsecase,
    userPersonalUsecase,
    organizationUsecase,
    accountUsecase,
    acctUsecase,
    bfUsecase,
    questionnaireUsecase,
  };

  const container = document.getElementById('app');
  let isRendered = false;

  const update = (state: State) => {
    const element = createElement(Root, { dependency, history, state, config });

    if (!isRendered) {
      isRendered = true;
      render(element, container);
      return;
    }

    hydrate(element, container);
  };

  history.listen(location => {
    // wait for document.title updates
    setTimeout(() => {
      googleAnalytics.sendPageView(document.title, window.location.href, location.pathname);
      execSitejs();
    });

    store.dispatch(setLocation(location));
  });

  store.addMiddleware(createKukerEmitterMiddleware());

  store.subscribe(() => update(store.getState()));

  update(store.getState());

  // set language
  const language = configurationStorage.loadLanguage() || window.navigator.language.toUpperCase();
  configurationChangingUsecase.changeLanguageTemporarily(language);

  // Send initial page view
  googleAnalytics.sendPageView(document.title, window.location.href, location.pathname);

  execSitejs();

  // embed kaizen-market
  window.addEventListener('message', event => {
    if (typeof event.data !== 'string') {
      return;
    }
    if (config.KAIZEN_MARKET_URL.indexOf(event.origin) === -1) {
      return;
    }
    const [pathStr, queryStr] = event.data.split('?');
    const queryParse = parse(queryStr);
    window.history.replaceState('', '', `${pathStr}${stringify(queryParse, { encode: false, addQueryPrefix: true })}`);
  });
};

window.document.addEventListener('DOMContentLoaded', main);
